import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, FileUp, FileDown, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { checkPairings } from '@/lib/api';
import { showErrorToast } from '@/lib/toast';
import type { FidePairingsCheckResult } from '@swiss-magic/shared-types';

// Story 101 — public, unauthenticated FIDE pairings checker (endorsement
// checklist service #3). Upload a TRF16; the backend runs the endorsed
// engine's check mode and we render the verdict + per-round discrepancies.
export function FidePairingsCheckerPage() {
  const { t } = useTranslation('tournament');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<FidePairingsCheckResult | null>(null);

  async function onCheck() {
    if (!file) return;
    setChecking(true);
    setResult(null);
    try {
      setResult(await checkPairings(file));
    } catch {
      showErrorToast(t('fide_checker.failed'));
    } finally {
      setChecking(false);
    }
  }

  function downloadChecklist() {
    if (!result?.checklist) return;
    const url = URL.createObjectURL(
      new Blob([result.checklist], { type: 'text/plain' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pairings-checklist.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid="fide-pairings-checker-page" className="min-h-screen bg-background">
      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="mb-1 text-2xl font-semibold">{t('fide_checker.title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t('fide_checker.subtitle')}
        </p>

        <Card>
          <CardContent className="space-y-3 py-6">
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={inputRef}
                type="file"
                accept=".trf,.txt"
                data-testid="fpc-file-input"
                className="hidden"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setResult(null);
                }}
              />
              <Button
                variant="outline"
                data-testid="fpc-choose-button"
                onClick={() => inputRef.current?.click()}
              >
                <FileUp className="mr-2 h-4 w-4" />
                {t('fide_checker.choose_button')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {file ? file.name : t('fide_checker.no_file')}
              </span>
              <Button
                data-testid="fpc-check-button"
                disabled={!file || checking}
                onClick={onCheck}
              >
                {checking ? t('fide_checker.checking') : t('fide_checker.check_button')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {result && (
          <Card className="mt-6">
            <CardContent className="space-y-4 py-6">
              {result.error ? (
                <p
                  data-testid="fpc-verdict-error"
                  className="flex items-center gap-2 text-sm font-medium text-destructive"
                >
                  <XCircle className="h-5 w-5" />
                  {t('fide_checker.verdict_error', { message: result.error })}
                </p>
              ) : result.valid ? (
                <p
                  data-testid="fpc-verdict-match"
                  className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  {t('fide_checker.verdict_match', { count: result.rounds.length })}
                </p>
              ) : (
                <p
                  data-testid="fpc-verdict-discrepancies"
                  className="flex items-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400"
                >
                  <AlertTriangle className="h-5 w-5" />
                  {t('fide_checker.verdict_discrepancies')}
                </p>
              )}

              {result.rounds
                .filter((r) => r.discrepancies.length > 0)
                .map((r) => (
                  <div key={r.round} data-testid={`fpc-round-${r.round}`}>
                    <h3 className="mb-1 text-sm font-semibold">
                      {t('fide_checker.round_heading', { round: r.round })}
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="py-1 pr-4">{t('fide_checker.col_engine')}</th>
                          <th className="py-1">{t('fide_checker.col_file')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.discrepancies.map((d, i) => (
                          <tr key={i} className="border-b">
                            <td className="py-1 pr-4 font-mono">{d.checker}</td>
                            <td className="py-1 font-mono">{d.tournament}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

              {result.checklist && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="fpc-download-checklist"
                  onClick={downloadChecklist}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  {t('fide_checker.download_checklist')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
